import AttachmentIcon from '@mui/icons-material/AttachFile';
import { IconButton, Tooltip } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SendIcon } from "../../assets/icons/arrow-narrow-up.svg";
import { ReactComponent as GlobeIcon } from "../../assets/icons/globe.svg";
import { ReactComponent as MicIcon } from "../../assets/icons/microphone.svg";
import { ReactComponent as SquareIcon } from "../../assets/icons/square.svg";
import { CHAT_CONSTANTS } from '../../config/chat.constants';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useVoiceRecognition } from '../../hooks/useVoiceRecognition';
import { useFileValidation } from '../../utils/fileValidation';
import {
    ActionsWrapper,
    CharacterCounter,
    Container,
    GlobeButton,
    InputActions,
    InputWrapper,
    MicrophoneButton,
    SendButtonWrapper,
    StyledForm,
    StyledTextField
} from './ChatInput.styled';
import { FileAttachmentList } from './FileAttachmentList';

interface ChatInputProps {
    onSendMessage: (text: string, files: File[] | null, searchInternet: boolean) => void;
    onStopGeneration?: () => void;
    isGenerating?: boolean;
    disabled?: boolean;
    conversationId?: string | null;
}

const ChatInput: React.FC<ChatInputProps> = ({
                                                 onSendMessage,
                                                 onStopGeneration,
                                                 isGenerating = false,
                                                 disabled = false,
                                                 conversationId
                                             }) => {
    const [text, setText] = useState('');
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const [searchInternet, setSearchInternet] = useState(true);
    const [isMultiLine, setIsMultiLine] = useState(false);
    const theme = useTheme();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const {validateFile} = useFileValidation();
    const {t} = useTranslation();

    const handleFilesAccepted = (files: File[]) => {
        setAttachedFiles(prevFiles => ([...prevFiles, ...files]));
    };

    useEffect(() => {
        setText('');
        setAttachedFiles([]);
    }, [conversationId]);

    const {
        isDraggingOver,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useDragAndDrop({
        onFileAccepted: handleFilesAccepted,
        validateFile,
        disabled: isGenerating || disabled,
        currentFiles: attachedFiles
    });

    useEffect(() => {
        const checkMultiLine = () => {
            if (text.length === 0) {
                if (isMultiLine) setIsMultiLine(false);
                return;
            }
            if (!isMultiLine && text.length > 50) {
                setIsMultiLine(true);
            } else if (isMultiLine && text.length < 30) {
                setIsMultiLine(false);
            }
        };
        checkMultiLine();
    }, [text, isMultiLine]);

    const handleSend = () => {
        if ((text.trim() || attachedFiles.length > 0) && !isGenerating && !disabled) {
            onSendMessage(text, attachedFiles.length > 0 ? attachedFiles : null, searchInternet);
            setText('');
            if (attachedFiles.length > 0) {
                handleRemoveAllFiles();
            }
        }
    };

    const { isRecording, handleVoiceRecognition } = useVoiceRecognition(
        setText,
        t('chatInput.voiceNotSupported')
    );

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const files = Array.from(event.target.files);
        const validFiles: File[] = [];

        for (const file of files) {
            const validationResult = validateFile(file);
            if (validationResult.isValid) {
                validFiles.push(file);
            }
        }

        if (validFiles.length > 0) {
            setAttachedFiles(prevFiles => ([...prevFiles, ...validFiles]));
        }
    };

    const handleRemoveFile = (index: number) => {
        setAttachedFiles(prevFiles => {
            const newFiles = [...prevFiles];
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const handleRemoveAllFiles = () => {
        setAttachedFiles([]);
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const hasFiles = attachedFiles.length > 0;

    return (
        <Container>
            <StyledForm
                ref={formRef}
                $hasFile={hasFiles}
                $isMultiLine={isMultiLine}
                $isDraggingOver={isDraggingOver}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <FileAttachmentList files={attachedFiles} onRemove={handleRemoveFile} />
                <InputWrapper>
                    {!isMultiLine && (
                        <InputActions $attachedFile={hasFiles} $isMultiLine={isMultiLine}>
                            <label
                                htmlFor="file-input"
                                aria-label={t('chatInput.attachFile') || ''}
                                style={{
                                    pointerEvents: isGenerating ? 'none' : 'auto',
                                    opacity: isGenerating ? '0.5' : '1'
                                }}>
                                <input
                                    id="file-input"
                                    type="file"
                                    multiple
                                    style={{display: 'none'}}
                                    onChange={isGenerating ? undefined : handleFileChange}
                                />
                                <IconButton
                                    component="span"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        padding: '5px',
                                        pointerEvents: isGenerating ? 'none' : 'auto'
                                    }}>
                                    <Tooltip title={t('chatInput.attachFile')}>
                                        <AttachmentIcon/>
                                    </Tooltip>
                                </IconButton>
                            </label>
                            <Tooltip title={t('chatInput.searchInternet')}>
                                <span>
                                    <GlobeButton
                                        onClick={() => setSearchInternet(v => !v)}
                                        $isInternetSelected={searchInternet}
                                        disabled={isGenerating || disabled}
                                    >
                                        <GlobeIcon/>
                                    </GlobeButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('chatInput.voiceRecording')}>
                                <span>
                                    <MicrophoneButton
                                        onClick={handleVoiceRecognition}
                                        $isRecording={isRecording}
                                        disabled={isGenerating || disabled}
                                    >
                                        <MicIcon/>
                                    </MicrophoneButton>
                                </span>
                            </Tooltip>
                        </InputActions>
                    )}
                    <StyledTextField
                        value={text}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setText(e.target.value)}
                        fullWidth
                        placeholder={t('chatInput.placeholder')}
                        variant="outlined"
                        multiline
                        maxRows={4}
                        $hasFile={hasFiles}
                        inputRef={inputRef}
                        inputProps={{maxLength: CHAT_CONSTANTS.MAX_MESSAGE_LENGTH}}
                        disabled={isGenerating || disabled}
                        style={{pointerEvents: (isGenerating || disabled) ? 'none' : 'auto'}}
                    />
                    {!isMultiLine && (
                        <SendButtonWrapper>
                            {isGenerating ? (
                                <Tooltip title={t('chatInput.stopGeneration')}>
                                    <IconButton
                                        onClick={onStopGeneration}
                                        sx={{
                                            color: theme.palette.grey[500],
                                            '& svg': {width: '20px', height: '20px'}
                                        }}
                                    >
                                        <SquareIcon/>
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <IconButton
                                    disabled={!text.trim() && !hasFiles}
                                    onClick={handleSend}
                                    style={{pointerEvents: 'auto'}}
                                >
                                    <SendIcon/>
                                </IconButton>
                            )}
                        </SendButtonWrapper>
                    )}
                </InputWrapper>

                {isMultiLine && (
                    <ActionsWrapper>
                        <InputActions $attachedFile={hasFiles} $isMultiLine={isMultiLine}>
                            <label
                                htmlFor="file-input"
                                aria-label={t('chatInput.attachFile') || ''}
                                style={{
                                    pointerEvents: isGenerating ? 'none' : 'auto',
                                    opacity: isGenerating ? '0.5' : '1'
                                }}>
                                <input
                                    id="file-input"
                                    type="file"
                                    multiple
                                    style={{display: 'none'}}
                                    onChange={isGenerating ? undefined : handleFileChange}
                                />
                                <IconButton
                                    component="span"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        padding: '5px',
                                        pointerEvents: isGenerating ? 'none' : 'auto'
                                    }}>
                                    <Tooltip title={t('chatInput.attachFile')}>
                                        <AttachmentIcon/>
                                    </Tooltip>
                                </IconButton>
                            </label>
                            <Tooltip title={t('chatInput.searchInternet')}>
                                <span>
                                    <GlobeButton
                                        onClick={() => setSearchInternet(v => !v)}
                                        $isInternetSelected={searchInternet}
                                        disabled={isGenerating || disabled}
                                    >
                                        <GlobeIcon/>
                                    </GlobeButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('chatInput.voiceRecording')}>
                                <span>
                                    <MicrophoneButton
                                        onClick={handleVoiceRecognition}
                                        $isRecording={isRecording}
                                        disabled={isGenerating || disabled}
                                    >
                                        <MicIcon/>
                                    </MicrophoneButton>
                                </span>
                            </Tooltip>
                        </InputActions>
                        <SendButtonWrapper>
                            {isGenerating ? (
                                <Tooltip title={t('chatInput.stopGeneration')}>
                                    <IconButton
                                        onClick={onStopGeneration}
                                        sx={{
                                            color: theme.palette.grey[500],
                                            '& svg': {width: '20px', height: '20px'}
                                        }}
                                    >
                                        <SquareIcon/>
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <IconButton
                                    disabled={(!text.trim() && !hasFiles) || disabled}
                                    onClick={handleSend}
                                    style={{pointerEvents: 'auto'}}
                                >
                                    <SendIcon/>
                                </IconButton>
                            )}
                        </SendButtonWrapper>
                    </ActionsWrapper>
                )}
            </StyledForm>
            <CharacterCounter>{text.length}/{CHAT_CONSTANTS.MAX_MESSAGE_LENGTH}</CharacterCounter>
        </Container>
    );
};

export default ChatInput;
